/**
 * Microcourse Studio — useAutoplay hook（fragment 级播放引擎）
 *
 * 核心模型：播放/导航的最小单位是 fragment（片段）
 * - 含讲解分段（segments）的 slide：fragment 数 = max(segments.length, elements.length)，
 *   每个 fragment 对应一段 TTS（若存在）并逐步揭示 elements
 * - 无分段的 slide：单个 fragment，整页显示（fragmentIndex = -1）
 * - stepNext / stepPrev：手动逐个 fragment 导航，并播放目标 fragment 的 TTS（打断当前语音）
 * - start：从指定 slide 自动连播（播完一个 fragment 自动推进下一个）
 */

import { useRef, useState, useCallback, useEffect } from 'react'
import type { MicroSlide } from '@ai-tutor/shared'
import { useTTS } from './useTTS'

export type AutoplayStatus = 'idle' | 'playing' | 'paused' | 'finished'

export interface AutoplayState {
  status: AutoplayStatus
  /** 当前 slide index */
  currentSlideIndex: number
  /** 当前 fragment index（-1 = 未开始/整页显示） */
  currentSegIndex: number
  /** 当前 fragment index（用于 SlideCanvas 逐步显示，-1 = 全部显示） */
  fragmentIndex: number
  /** 状态文本 */
  statusText: string
}

export interface UseAutoplayOptions {
  slides: MicroSlide[]
  /** 无 segments 的 slide 停留时间 (ms) */
  noSegmentDelay?: number
  /** 播放进度回调 (slideIndex, totalSlides) */
  onProgress?: (slideIndex: number, totalSlides: number) => void
  /** 播放完毕回调 */
  onFinish?: () => void
  /** slide 切换回调 */
  onSlideChange?: (slideIndex: number) => void
}

export interface UseAutoplayReturn extends AutoplayState {
  /** TTS speaking 状态 */
  speaking: boolean
  /** 是否可向前（非第一个 fragment） */
  canPrev: boolean
  /** 是否可向后（非最后一个 fragment） */
  canNext: boolean
  /** 开始自动连播（从指定 slide 的第一个 fragment） */
  start: (fromSlideIndex?: number) => void
  /** 暂停 */
  pause: () => void
  /** 恢复 */
  resume: () => void
  /** 停止 */
  stop: () => void
  /** 跳转到指定 slide 的第一个 fragment（保留当前连播/手动模式） */
  goToSlide: (index: number) => void
  /** 手动步进：下一个 fragment（跨 slide 时为下一 slide 的第一个 fragment），并播放其 TTS */
  stepNext: () => void
  /** 手动步进：上一个 fragment（跨 slide 时为上一 slide 的最后一个 fragment），并播放其 TTS */
  stepPrev: () => void
}

// ─── 纯函数工具 ───────────────────────────────────────────────────

/** slide 是否含有效讲解分段 */
function slideHasSegments(slide?: MicroSlide): boolean {
  return (
    !!slide?.segments &&
    slide.segments.length > 0 &&
    slide.segments.some((s) => !!s.ttsText && s.ttsText.trim().length > 0)
  )
}

/** slide 的 fragment 数量（= TTS 分段数，每个 fragment 对应一段语音） */
function slideFragCount(slide?: MicroSlide): number {
  if (!slide) return 0
  if (!slideHasSegments(slide)) return 1
  return slide.segments!.length
}

/**
 * fragment f 累计应显示的元素数量（把元素均分到各 fragment）
 * 保证 TTS 朗读到某段时，该段覆盖的内容已同步出现在画面上
 */
export function fragmentElementVisibleCount(slide: MicroSlide | undefined, fragmentIndex: number): number {
  if (!slide) return 0
  const n = slide.elements?.length ?? 0
  if (fragmentIndex < 0) return n
  if (!slideHasSegments(slide)) return n
  const N = slide.segments!.length
  if (N <= 0) return n
  // 无富余 fragment 时退化为按索引递增
  if (n <= N) return Math.min(fragmentIndex + 1, n)
  return Math.min(n, Math.floor(((fragmentIndex + 1) * n) / N))
}

export function useAutoplay({
  slides,
  noSegmentDelay = 2000,
  onProgress,
  onFinish,
  onSlideChange,
}: UseAutoplayOptions): UseAutoplayReturn {
  const [status, setStatus] = useState<AutoplayStatus>('idle')
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [currentSegIndex, setCurrentSegIndex] = useState(-1)
  const [fragmentIndex, setFragmentIndex] = useState(-1)
  const [statusText, setStatusText] = useState('')

  const tts = useTTS()

  // Refs（避免闭包过期）
  const slidesRef = useRef(slides)
  slidesRef.current = slides
  const cbRef = useRef({ onProgress, onFinish, onSlideChange })
  cbRef.current = { onProgress, onFinish, onSlideChange }
  const noSegmentDelayRef = useRef(noSegmentDelay)
  noSegmentDelayRef.current = noSegmentDelay

  /** 当前位置（逻辑 fragment，-1 = 未开始） */
  const posRef = useRef<{ s: number; f: number }>({ s: 0, f: -1 })
  /** 是否自动推进（连播模式） */
  const autoAdvanceRef = useRef(false)
  /** 运行 id，用于作废旧的异步回调 */
  const runIdRef = useRef(0)
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const playFragmentRef = useRef<(s: number, f: number, auto: boolean) => void>(() => {})

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current)
      fallbackTimerRef.current = null
    }
  }, [])

  // Unmount 清理
  useEffect(() => {
    return () => {
      runIdRef.current++
      autoAdvanceRef.current = false
      clearFallbackTimer()
    }
  }, [clearFallbackTimer])

  /** 标记播放完毕 */
  const finish = useCallback(() => {
    runIdRef.current++
    autoAdvanceRef.current = false
    clearFallbackTimer()
    setStatus('finished')
    setStatusText('讲解播放完毕')
    setFragmentIndex(-1)
    cbRef.current.onFinish?.()
  }, [clearFallbackTimer])

  /** 从 (s, f) 推进到下一个 fragment（自动连播） */
  const goNext = useCallback(
    (s: number, f: number) => {
      const list = slidesRef.current
      const count = slideFragCount(list[s])
      if (f < count - 1) {
        playFragmentRef.current(s, f + 1, true)
      } else if (s < list.length - 1) {
        playFragmentRef.current(s + 1, 0, true)
      } else {
        finish()
      }
    },
    [finish]
  )

  /** 播放指定 fragment；autoAdvance 决定播完是否自动推进 */
  const playFragment = useCallback(
    (s: number, f: number, autoAdvance: boolean) => {
      const runId = ++runIdRef.current
      autoAdvanceRef.current = autoAdvance
      clearFallbackTimer()
      tts.stop() // 打断当前语音（TTS 与 fragment 绑定）

      const list = slidesRef.current
      const slide = list[s]
      if (!slide) {
        finish()
        return
      }

      posRef.current = { s, f }
      setCurrentSlideIndex(s)
      setCurrentSegIndex(f)
      setFragmentIndex(slideHasSegments(slide) ? f : -1)
      cbRef.current.onSlideChange?.(s)
      cbRef.current.onProgress?.(s, list.length)

      // 无讲解分段：整页显示
      if (!slideHasSegments(slide)) {
        setStatusText(`第 ${s + 1} 张：${slide.title || ''}`)
        if (autoAdvance) {
          fallbackTimerRef.current = setTimeout(() => {
            if (runIdRef.current !== runId) return
            goNext(s, f)
          }, noSegmentDelayRef.current)
        }
        return
      }

      const seg = slide.segments![f]
      const total = slide.segments!.length
      setStatusText(`第 ${s + 1} 张 · 段 ${Math.min(f + 1, total)}/${total}`)

      const playNextAfter = () => {
        if (runIdRef.current !== runId) return
        if (autoAdvanceRef.current) goNext(s, f)
      }

      // 该 fragment 无对应 TTS（如仅用于揭示 element）：连播时短暂停留
      if (!seg || !seg.ttsText || !seg.ttsText.trim()) {
        if (autoAdvance) {
          fallbackTimerRef.current = setTimeout(playNextAfter, 300)
        }
        return
      }

      // 播放 TTS，结束后按需推进
      tts.playSegment(slide.id, f, seg.ttsText, seg.audioUrl).then(playNextAfter)
    },
    [tts, clearFallbackTimer, goNext, finish]
  )
  playFragmentRef.current = playFragment

  const start = useCallback(
    (fromSlideIndex = 0) => {
      setStatus('playing')
      playFragment(fromSlideIndex, 0, true)
    },
    [playFragment]
  )

  const pause = useCallback(() => {
    autoAdvanceRef.current = false
    runIdRef.current++
    clearFallbackTimer()
    tts.pause()
    setStatus('paused')
    setStatusText('已暂停')
  }, [tts, clearFallbackTimer])

  const resume = useCallback(() => {
    setStatus('playing')
    autoAdvanceRef.current = true
    const { s, f } = posRef.current
    playFragment(s, Math.max(0, f), true)
  }, [playFragment])

  const stop = useCallback(() => {
    runIdRef.current++
    autoAdvanceRef.current = false
    clearFallbackTimer()
    tts.stop()
    setStatus('idle')
    setCurrentSegIndex(-1)
    setFragmentIndex(-1)
    setStatusText('已停止')
  }, [tts, clearFallbackTimer])

  const goToSlide = useCallback(
    (index: number) => {
      playFragment(index, 0, autoAdvanceRef.current)
    },
    [playFragment]
  )

  /** 手动步进：下一个 fragment（保留当前连播/手动模式） */
  const stepNext = useCallback(() => {
    const { s, f } = posRef.current
    const list = slidesRef.current
    const count = slideFragCount(list[s])
    const auto = autoAdvanceRef.current
    if (f < count - 1) {
      playFragmentRef.current(s, f + 1, auto)
    } else if (s < list.length - 1) {
      playFragmentRef.current(s + 1, 0, auto)
    }
  }, [])

  /** 手动步进：上一个 fragment（保留当前连播/手动模式） */
  const stepPrev = useCallback(() => {
    const { s, f } = posRef.current
    const list = slidesRef.current
    const auto = autoAdvanceRef.current
    if (f > 0) {
      playFragmentRef.current(s, f - 1, auto)
    } else if (s > 0) {
      const prevCount = slideFragCount(list[s - 1])
      playFragmentRef.current(s - 1, prevCount - 1, auto)
    } else {
      playFragmentRef.current(0, 0, auto)
    }
  }, [])

  // 边界判断
  const total = slides.length
  const curCount = slideFragCount(slides[currentSlideIndex])
  const canPrev = currentSlideIndex > 0 || currentSegIndex > 0
  const canNext =
    total > 0 && !(currentSlideIndex >= total - 1 && currentSegIndex >= curCount - 1)

  return {
    status,
    currentSlideIndex,
    currentSegIndex,
    fragmentIndex,
    statusText,
    speaking: tts.speaking,
    canPrev,
    canNext,
    start,
    pause,
    resume,
    stop,
    goToSlide,
    stepNext,
    stepPrev,
  }
}
